/**
 * Product luxury: carousel scroll + scrubber, tabs, variant sync, fullscreen lightbox.
 * Desktop (≥1200px) shows two slides per page — pagination indexes by page.
 */

const LUXURY_DESKTOP_MQ = '(min-width: 1200px)';

function luxurySlidesPerView() {
  return window.matchMedia(LUXURY_DESKTOP_MQ).matches ? 2 : 1;
}

function luxuryPageStep(track, slides) {
  const slide = slides[0];
  if (!slide) return track.clientWidth || 1;
  const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
  const perView = luxurySlidesPerView();
  return slide.offsetWidth * perView + gap * Math.max(0, perView - 1);
}

function updateLuxuryCarousel(track, paginationRoot) {
  if (!track || !paginationRoot) return;
  const slides = track.querySelectorAll('.product-luxury-view__slide');
  const total = slides.length;
  if (total <= 1) return;

  const perView = luxurySlidesPerView();
  const totalPages = Math.max(1, Math.ceil(total / perView));
  const step = luxuryPageStep(track, slides);
  if (!step) return;

  let page = Math.round(track.scrollLeft / step);
  page = Math.max(0, Math.min(totalPages - 1, page));

  paginationRoot.style.setProperty('--luxury-slide-index', String(page));
  paginationRoot.style.setProperty('--luxury-slide-count', String(totalPages));

  const currentEl = paginationRoot.querySelector('[data-luxury-current]');
  const totalEl = paginationRoot.querySelector('[data-luxury-total]');
  if (currentEl) currentEl.textContent = String(page + 1);
  if (totalEl) totalEl.textContent = String(totalPages);
  paginationRoot.hidden = totalPages <= 1;
}

function initLuxuryLightbox(root) {
  const lightbox = root.querySelector('[data-luxury-lightbox]');
  if (!lightbox || lightbox.dataset.luxuryLightboxInited === 'true') return;
  lightbox.dataset.luxuryLightboxInited = 'true';

  const track = lightbox.querySelector('[data-luxury-lightbox-track]');
  const slides = lightbox.querySelectorAll('[data-luxury-lightbox-slide]');
  const currentEl = lightbox.querySelector('[data-luxury-lightbox-current]');
  const totalEl = lightbox.querySelector('[data-luxury-lightbox-total]');
  const closeBtn = lightbox.querySelector('[data-luxury-lightbox-close]');
  const prevBtn = lightbox.querySelector('[data-luxury-lightbox-prev]');
  const nextBtn = lightbox.querySelector('[data-luxury-lightbox-next]');
  const total = slides.length;
  if (!track || total === 0) return;

  if (totalEl) totalEl.textContent = String(total);

  let index = 0;
  let scrollRaf = 0;

  function clampIndex(i) {
    return Math.max(0, Math.min(total - 1, i));
  }

  function updateCounter(i) {
    if (currentEl) currentEl.textContent = String(i + 1);
  }

  function goTo(i, behavior) {
    index = clampIndex(i);
    const slide = slides[index];
    if (!slide) return;
    track.scrollTo({ left: slide.offsetLeft, behavior: behavior || 'smooth' });
    updateCounter(index);
  }

  function syncFromScroll() {
    const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
    index = clampIndex(i);
    updateCounter(index);
  }

  function openAt(i) {
    index = clampIndex(i);
    updateCounter(index);
    if (typeof lightbox.showModal === 'function') {
      lightbox.showModal();
    } else {
      lightbox.setAttribute('open', '');
    }
    document.documentElement.classList.add('product-luxury-lightbox-open');
    requestAnimationFrame(function () {
      goTo(index, 'instant');
    });
  }

  function closeLightbox() {
    if (typeof lightbox.close === 'function' && lightbox.open) {
      lightbox.close();
    } else {
      lightbox.removeAttribute('open');
    }
    document.documentElement.classList.remove('product-luxury-lightbox-open');
    lightbox.querySelectorAll('video').forEach(function (v) {
      try {
        v.pause();
      } catch {
        /* ignore */
      }
    });
  }

  root.querySelectorAll('[data-luxury-zoom-open]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      const i = Number(btn.getAttribute('data-luxury-zoom-index') || '0');
      openAt(i);
    });
  });

  if (closeBtn) closeBtn.addEventListener('click', closeLightbox);
  if (prevBtn) {
    prevBtn.addEventListener('click', function () {
      goTo(index - 1);
    });
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', function () {
      goTo(index + 1);
    });
  }

  track.addEventListener(
    'scroll',
    function () {
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(function () {
        scrollRaf = 0;
        syncFromScroll();
      });
    },
    { passive: true }
  );

  lightbox.addEventListener('cancel', function (e) {
    e.preventDefault();
    closeLightbox();
  });

  lightbox.addEventListener('click', function (e) {
    if (e.target === lightbox) closeLightbox();
  });

  lightbox.addEventListener('keydown', function (e) {
    if (!lightbox.open && !lightbox.hasAttribute('open')) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(index + 1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeLightbox();
    }
  });
}

function initProductLuxuryView(root) {
  if (!root || root.dataset.luxuryInited === 'true') return;
  root.dataset.luxuryInited = 'true';

  const productId = root.dataset.productId;
  const track = root.querySelector('[data-product-luxury-track]');
  const paginationRoot = root.querySelector('[data-luxury-pagination]');
  const desktopMq = window.matchMedia(LUXURY_DESKTOP_MQ);

  /*
   * The purchase panel first lives in the page flow after the description.
   * Once the description has been passed, the same panel becomes the floating
   * purchase control. The anchor preserves its document space during the swap.
   */
  const stickyPurchase = root.querySelector('[data-luxury-sticky]');
  const stickyPurchaseAnchor = root.querySelector('[data-luxury-sticky-anchor]');
  const descriptionBoundary = root.querySelector('.product-luxury-view__panels');

  if (stickyPurchase) {
    stickyPurchase.setAttribute('aria-hidden', 'false');

    const setStickyPurchaseFloating = function (floating) {
      stickyPurchase.classList.toggle('is-floating', floating);
    };

    const syncStickyAnchorHeight = function () {
      if (!stickyPurchaseAnchor) return;
      stickyPurchaseAnchor.style.setProperty(
        '--luxury-sticky-inline-height',
        stickyPurchase.offsetHeight + 'px'
      );
    };

    syncStickyAnchorHeight();
    requestAnimationFrame(syncStickyAnchorHeight);

    if (stickyPurchaseAnchor && typeof ResizeObserver !== 'undefined') {
      const stickySizeObserver = new ResizeObserver(syncStickyAnchorHeight);
      stickySizeObserver.observe(stickyPurchase);
    }

    if (descriptionBoundary && typeof IntersectionObserver !== 'undefined') {
      const stickyFloatObserver = new IntersectionObserver(
        function (entries) {
          const entry = entries[0];
          if (!entry) return;

          const hasPassedDescription =
            !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          setStickyPurchaseFloating(hasPassedDescription);
        },
        { threshold: 0 }
      );

      stickyFloatObserver.observe(descriptionBoundary);
    } else {
      setStickyPurchaseFloating(false);
    }
  }

  let raf = 0;
  function onScroll() {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(function () {
      raf = 0;
      updateLuxuryCarousel(track, paginationRoot);
    });
  }

  if (track && paginationRoot) {
    const slides = track.querySelectorAll('.product-luxury-view__slide');
    if (slides.length > 1) {
      track.addEventListener('scroll', onScroll, { passive: true });
      track.addEventListener('scrollend', onScroll);
      window.addEventListener('resize', onScroll);
      if (typeof desktopMq.addEventListener === 'function') {
        desktopMq.addEventListener('change', onScroll);
      } else if (typeof desktopMq.addListener === 'function') {
        desktopMq.addListener(onScroll);
      }
      updateLuxuryCarousel(track, paginationRoot);
    }
  }

  initLuxuryLightbox(root);

  const variantScript = root.querySelector('[data-luxury-variants-json]');
  let variantMap = {};
  if (variantScript && variantScript.textContent) {
    try {
      variantMap = JSON.parse(variantScript.textContent);
    } catch {
      variantMap = {};
    }
  }

  const variantInput = root.querySelector('[data-luxury-variant-input]');
  const priceEl = root.querySelector('[data-luxury-price-text]');
  const addCart = root.querySelector('add-to-cart-component');

  document.addEventListener('variant:update', function (e) {
    if (String(e.detail?.data?.productId) !== String(productId)) return;
    const r = e.detail.resource;
    if (!r || !r.id) return;
    if (variantInput) variantInput.value = r.id;
    const entry = variantMap[String(r.id)];
    if (priceEl && entry && entry.price) {
      priceEl.textContent = entry.price;
    }
    if (addCart) {
      if (r.available) {
        addCart.enable?.();
      } else {
        addCart.disable?.();
      }
    }
    const qtyComponent = root.querySelector('quantity-selector-component');
    if (qtyComponent) {
      qtyComponent.setAttribute('data-variant-id', String(r.id));
      if (typeof qtyComponent.updateConstraints === 'function' && r.quantity_rule) {
        const qr = r.quantity_rule;
        const min = String(qr.min != null ? qr.min : 1);
        const max = qr.max != null ? String(qr.max) : null;
        const step = String(qr.increment != null ? qr.increment : 1);
        qtyComponent.updateConstraints(min, max, step);
      }
    }
  });

  const tablist = root.querySelector('.product-luxury-view__tabs');
  const panels = root.querySelectorAll('[data-luxury-panel]');

  function activateLuxuryTab(key) {
    const tabs = root.querySelectorAll('[data-luxury-tab]');
    tabs.forEach(function (t) {
      const on = t.getAttribute('data-luxury-tab') === key;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    panels.forEach(function (p) {
      const on = p.getAttribute('data-luxury-panel') === key;
      p.classList.toggle('is-active', on);
      p.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
  }

  if (tablist) {
    tablist.addEventListener('click', function (e) {
      // Clicks on button label text can set target to a Text node (no .closest) — breaks tab switching.
      const raw = e.target;
      const fromEl = raw instanceof Element ? raw : raw.parentElement;
      if (!fromEl) return;
      const tab = fromEl.closest('[data-luxury-tab]');
      if (!tab || !tablist.contains(tab)) return;
      e.preventDefault();
      const key = tab.getAttribute('data-luxury-tab');
      if (!key) return;
      activateLuxuryTab(key);
    });
  }

  /**
   * Move Shopify's real "More payment options" control from the main product form into the sticky bar slot
   * so it stays functional (same checkout flow as dynamic checkout).
   */
  const slot = root.querySelector('[data-luxury-payment-options-slot]');

  function findPrimaryProductFormForPaymentLink() {
    const pid = String(productId);
    const scoped =
      document.querySelector(
        `[data-testid="product-information"] product-form-component[data-product-id="${pid}"]`
      ) || document.querySelector(`#MainContent product-form-component[data-product-id="${pid}"]`);
    if (scoped && !root.contains(scoped)) return scoped;
    const all = document.querySelectorAll(`product-form-component[data-product-id="${pid}"]`);
    for (let i = 0; i < all.length; i++) {
      const f = all[i];
      if (!root.contains(f)) return f;
    }
    return null;
  }

  function isNodeRelocatable(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.closest('[data-checkout-payment-host]')) return true;
    if (node.getAttribute('aria-hidden') === 'true') return false;
    const cs = getComputedStyle(node);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05) return false;
    return true;
  }

  let relocatePaymentTimer = 0;
  function debouncedRelocatePaymentLink() {
    clearTimeout(relocatePaymentTimer);
    relocatePaymentTimer = setTimeout(relocateLuxuryPaymentOptionsLink, 60);
  }

  function relocateLuxuryPaymentOptionsLink() {
    if (!slot) return;
    const mainForm = findPrimaryProductFormForPaymentLink();
    if (!mainForm) {
      return;
    }

    let node = /** @type {HTMLElement | null} */ (
      mainForm.querySelector('[data-checkout-payment-host] more-payment-options-link')
    );
    if (!node) {
      node = mainForm.querySelector('[data-checkout-payment-host] .shopify-payment-button__more-options');
    }
    if (!node) {
      node = mainForm.querySelector('more-payment-options-link');
    }
    if (!node) {
      node = mainForm.querySelector('.shopify-payment-button__more-options');
    }
    if (!node || !isNodeRelocatable(node)) {
      return;
    }

    if (slot.contains(node)) {
      return;
    }

    slot.appendChild(node);
  }

  if (slot) {
    const mainForm = findPrimaryProductFormForPaymentLink();
    if (mainForm && typeof MutationObserver !== 'undefined') {
      const mo = new MutationObserver(() => debouncedRelocatePaymentLink());
      mo.observe(mainForm, { childList: true, subtree: true });
    }

    document.addEventListener('variant:update', function (e) {
      if (String(e.detail?.data?.productId) !== String(productId)) return;
      debouncedRelocatePaymentLink();
    });

    if (typeof customElements !== 'undefined' && customElements.whenDefined) {
      customElements.whenDefined('more-payment-options-link').then(() => debouncedRelocatePaymentLink());
    }

    debouncedRelocatePaymentLink();
    window.addEventListener('load', debouncedRelocatePaymentLink);
    setTimeout(debouncedRelocatePaymentLink, 250);
    setTimeout(debouncedRelocatePaymentLink, 1200);
  }
}

function initAllProductLuxuryViews() {
  document.querySelectorAll('.product-luxury-view').forEach(initProductLuxuryView);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllProductLuxuryViews);
} else {
  initAllProductLuxuryViews();
}

document.addEventListener('shopify:section:load', function (event) {
  const r = event.target?.querySelector?.('.product-luxury-view');
  if (r) {
    r.dataset.luxuryInited = 'false';
    const lb = r.querySelector('[data-luxury-lightbox]');
    if (lb) lb.dataset.luxuryLightboxInited = 'false';
    initProductLuxuryView(r);
  }
});
